<template>
    <SkuStoneSearch
        v-model:sorting="sorting"
        :get-stone-route
        :active-filter-count
        :bounds
        :category="StoneCategoryCode.Gemstone"
        :data
        :errors
        :fetching
        :filter-chips
        :get-sort-route
        :has-bad-facets
        :hash-params
        :page
        :pagination-to
        :products-per-page="limit"
        :response
        :route-reset
        :shape
        :stale
        :title="t('diamond_search.gemstone_search')"
        is-gemstone
        @close-stone-modal="closeStoneModal"
    >
        <template #before-filters>
            <CmsBlocks
                :data="data?.pageProductListing?.beforeFilters || []"
                page-type="product-listing"
            />
        </template>

        <template #filters-quick>
            <ShapeFilter
                v-if="shapes"
                v-qa="'filter-shape'"
                :get-route="getRouteShape"
                :locked-to="ringShape"
                :selected-shape="shape"
                :shapes
            />
        </template>

        <template #after-grid>
            <CmsBlocks
                :data="data?.pageProductListing?.afterGrid || []"
                page-type="product-listing"
            />
        </template>

        <template #filters>
            <GemstoneFilters
                v-if="bounds"
                v-model:values="filters"
                :bounds
                :shape
            >
                <template #price>
                    <FilterPriceRange
                        v-if="bounds && priceBounds"
                        v-model="priceInCurrency"
                        v-qa="'filter-price'"
                        :bounds="priceBounds.bounds"
                        :currency="priceBounds.currency"
                    />
                </template>

                <template #stone-type>
                    <StoneSearchTabs
                        :shape="gemsApiShapeToShapeCode(shape)"
                        class="tabs"
                    />
                </template>
            </GemstoneFilters>

            <FilterBase
                v-qa="'checkboxes'"
                class="checkboxes"
            >
                <ThCheckbox
                    v-qa="'photo'"
                    :model-value="filters.withOriginCountry"
                    label="Only show diamonds with known origin country"
                    large
                    @update:model-value="
                        filters = {
                            ...filters,
                            isCanadaMark: $event || null,
                        }
                    "
                />
            </FilterBase>
        </template>

        <template #last-page-filters>
            <GemstoneFilters
                v-if="bounds"
                v-model:values="filters"
                :bounds
                :shape
            />
        </template>

        <template #no-results-found>
            <ThTitle
                alignment="CENTER"
                color="var(--blue)"
                size="SMALL"
                tag="H3"
            >
                We couldn't find any gemstones matching your current search
                criteria.
            </ThTitle>

            <ThSpacer size="SMALL" />

            <ThText
                alignment="CENTER"
                color="var(--slate)"
                size="MEDIUM"
            >
                <p>
                    There are natural diamonds available for this shape –
                    <ThLink
                        :to="
                            routes.diamondSearch(GemsApi_StoneCategory.Diamond)
                        "
                    >
                        return to the natural diamond search.
                    </ThLink>
                </p>

                <p>
                    Please
                    <ThLink @click="openSpeakToExpertModal">contact us</ThLink>
                    if you have a specific diamond search request and we can
                    help you with your enquiry.
                </p>
            </ThText>
        </template>
    </SkuStoneSearch>
</template>

<script lang="ts">
function getShapeIfSupportedInBounds(
    boundsInternal: GemsBoundsFragment | null | undefined,
    hashParams: DecodedStoneFiltersAndIdentifier | undefined
): GemsApi_StoneShape[] | null {
    if (!hashParams) {
        return null
    }

    const shape = hashParams.filters?.shape

    if (!shape) {
        return null
    }

    if (!boundsInternal) {
        return shape
    }

    const filtered = shape.filter((shape) =>
        boundsInternal.shapes.some((s) => s.code === shape)
    )

    if (filtered.length) {
        return filtered
    }

    return null
}
</script>

<script lang="ts" setup>
import {
    computed,
    type ComputedRef,
    defineAsyncComponent,
    hydrateOnVisible,
    ref,
    toRef,
} from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
    decodeFiltersAndStoneIdentifier,
    usePriceBounds,
    type DecodedStoneFiltersAndIdentifier,
} from '@/domain/stone'
import type { GemstoneSearchFilters } from '@/domain/stone/gemstone'
import { useResponseError } from '@/helpers/errors'
import type { GemsBoundsFragment } from '@/graphql/api'
import {
    GemsApi_StoneCategory,
    type GemsApi_StoneShape,
    type GemsApi_StoneSorting,
    type GemsFindStonesQueryVariables,
    StoneCategoryCode,
} from '@/graphql/api'
import { useTranslator } from '@/i18n'
import { useCountryPlugin } from '@/plugins/country.ts'
import { useRoutes } from '@/router/routes'
import { defaultCurrency } from '@/plugins/currency'
import { useRobotsPlugin } from '@/plugins/robots'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import { useRingShapeConstraintForGemsAPI } from '@/domain/stone/search/useRingShapeConstraintForGemsAPI.ts'
import { useActivated } from '@/composables/useActivated'
import { useFiltersCount } from '@/views/product/useFiltersCount'
import { useSpeakToExpertModal } from '@/composables/app/useSpeakToExpertModal'
import type { FilterChip } from '@/components/ui/filter/FilterChips.vue'
import { useFormattedPriceRange } from '@/composables/useMoneyFormatter'
import { getClearFilter } from '@/helpers/filter'
import { formatNumericProperty } from '@/domain/stone/labels'
import { useRoutePathWatcher } from '@/composables/route-path-watcher.ts'
import { usePageWatcher } from '@/composables/page-watcher.ts'
import { useObjectComputed } from '@/composables/useObjectComputed.ts'
import { allowedShapes } from '@/domain/stone/search/allowed-shapes.ts'
import { useGemsFindStonesQuery } from '@/graphql/api/stone/gems/GemsFindStones.query.generated.ts'
import { systemCodeToWords } from '@/helpers'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { gemsApiShapeToShapeCode } from '@/domain/stone/search/sku/shape'
import {
    getGemstoneTypeNameNew,
    isGemstoneTypeFilterInputSameAsFilter,
} from '@/domain/stone/search/sku/gemstone'

import GemstoneFilters from '@/components/stone/search/sku/GemstoneFilters.vue'
import FilterPriceRange from '@/components/stone/search/sku/filter/FilterPriceRange.vue'
import ShapeFilter from '@/components/stone/search/ShapeFilter.vue'
import FilterBase from '@/components/ui/filter/filters/layout/FilterBase.vue'
import ThCheckbox from '@/components/ui/inputs/ThCheckbox.vue'
import SkuStoneSearch from '@/components/stone/search/sku/SkuStoneSearch.vue'
import ThSpacer from '@/components/ui/ThSpacer.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import ThText from '@/components/ui/ThText.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import StoneSearchTabs from '@/components/stone/search/StoneSearchTabs.vue'
import { useWatchRedirectImmediate } from '@/helpers/redirect.ts'

const CmsBlocks = defineAsyncComponent({
    loader: () => import('@/components/cms/CmsBlocks.vue'),
    hydrate: hydrateOnVisible(),
})

const { page = 1, hash } = defineProps<{
    page?: number
    hash: string
}>()

const router = useRouter()
const { countryCode } = useCountryPlugin()
const { locale } = useTranslator()
const currency = useCurrencyInput()
const routes = useRoutes()
const { t } = useI18n()
const isAdmin = useIsAdmin()
const redirect = useRedirect()

const robots = useRobotsPlugin()
robots.set({
    index: true,
    follow: false,
})

const { open: openSpeakToExpertModal } = useSpeakToExpertModal()

const hashParams = computed(() =>
    decodeFiltersAndStoneIdentifier(hash.substring(1))
)

const defaultFilters: Required<GemstoneSearchFilters> = {
    priceInCurrency: null,
    weight: null,
    gemstoneTypes: [],

    shape: null,

    sorting: null,

    isCanadaMark: null,
    // hasPhoto: null,
    withOriginCountry: null,
    deliveryDays: null,
}

const filters = computed<Required<GemstoneSearchFilters>>({
    get: () => {
        const filters = hashParams.value?.filters
        const s = getShapeIfSupportedInBounds(
            boundsInternal.value,
            hashParams.value
        )

        if (filters) {
            return {
                ...defaultFilters,
                ...filters,
                shape: s,
            }
        }

        return { ...defaultFilters }
    },
    set: (filters) => {
        router.push(
            routes.gemstoneSearch(
                filters,
                undefined,
                hashParams.value?.stoneIdentifier
            )
        )
    },
})

const gemstoneTypes = toRef(() => filters.value?.gemstoneTypes || [])
const shape = toRef(() => filters.value?.shape?.[0])

const pathWatcher = useRoutePathWatcher(
    () => {
        return router.resolve(routes.gemstoneSearch(undefined, page))
    },
    () => {
        return router.resolve(
            routes.gemstoneSearch(
                // Prevent the SSR server from setting default values on the hash filters
                import.meta.env.SSR ? undefined : hashParams.value?.filters,
                page,
                hashParams.value?.stoneIdentifier
            )
        ).fullPath
    },
    301
)

const sorting = computed({
    get(): GemsApi_StoneSorting {
        return filters.value.sorting || { field: 'PRICE', direction: 'ASC' }
    },
    set(v: GemsApi_StoneSorting): void {
        filters.value = {
            ...filters.value,
            sorting: v,
        }
    },
})

const activeFilterCount = useFiltersCount(
    computed(() => [
        !ringShape.value && !!shape.value,
        !!filters.value.gemstoneTypes?.length,
        !!filters.value.withOriginCountry,
        !!(
            filters.value.priceInCurrency?.max ||
            filters.value.priceInCurrency?.min
        ),
        !!filters.value.weight,
    ])
)

const routeReset = computed(() => {
    return routes.gemstoneSearch({
        shape: ringShape.value?.systemCode && [ringShape.value?.systemCode],
    })
})

const formattedPriceRange = useFormattedPriceRange(
    toRef(() => filters.value.priceInCurrency)
)

const filterChips = computed(() => {
    const chips: FilterChip[] = []

    if (formattedPriceRange.value) {
        chips.push({
            label: formattedPriceRange.value,
            onRemove: getClearFilter(filters, 'priceInCurrency'),
        })
    }

    if (!ringShape.value && shape.value) {
        chips.push({
            label:
                shapes.value?.find((s) => s.systemCode === shape.value)
                    ?.translation?.name || systemCodeToWords(shape.value),
            getRouteRemove: getRouteShape,
        })
    }

    if (filters.value.weight?.min && filters.value.weight?.max) {
        chips.push({
            label: `${t('diamond_search.filter.carat_size.chip', {
                min: formatNumericProperty(filters.value.weight.min),
                max: formatNumericProperty(filters.value.weight.max),
            })}`,
            onRemove: getClearFilter(filters, 'weight'),
        })
    }

    if (gemstoneTypes.value) {
        gemstoneTypes.value.forEach((type) => {
            const a = bounds.value?.gemstoneType?.find((t) =>
                isGemstoneTypeFilterInputSameAsFilter(type, t)
            )

            chips.push({
                label: t('diamond_search.filter.gemstone_type.chip', {
                    type: a && getGemstoneTypeNameNew(a),
                }),
                // @ts-expect-error needs better types
                onRemove: getClearFilter(filters, 'gemstoneTypes', type),
            })
        })
    }

    if (filters.value.deliveryDays) {
        chips.push({
            label: `${t('diamond_search.filter.deliveryDays.chip', {
                deliveryDays: filters.value.deliveryDays,
            })}`,
            onRemove: getClearFilter(filters, 'deliveryDays'),
        })
    }

    return chips
})

const getSortRoute = (sorting: GemsApi_StoneSorting): ButtonRouteRelative =>
    routes.gemstoneSearch({
        ...filters.value,
        sorting,
    })

const priceInCurrency = computed({
    get: () => filters.value.priceInCurrency,
    set: (priceInCurrency) => {
        filters.value = { ...filters.value, priceInCurrency }
    },
})

const limit = 19

const getRouteShape = (shape?: GemsApi_StoneShape) => {
    return routes.gemstoneSearch(
        {
            ...filters.value,
            shape: shape ? [shape] : undefined,
        },
        page,
        hashParams.value?.stoneIdentifier
    )
}

const boundsInternal = ref<GemsBoundsFragment | null>(null)

const filtersVariable = useObjectComputed(() => {
    return {
        ...defaultFilters,
        ...filters.value,
        sorting: {
            direction: filters.value?.sorting?.direction || 'ASC',
            field: filters.value?.sorting?.field || 'PRICE',
        },
        isConsultant: isAdmin.value,
        category: [StoneCategoryCode.Gemstone],
        shape: shape.value ? [shape.value] : [...allowedShapes],
        gemstoneTypes: gemstoneTypes.value,
        country: countryCode.value,
        pagination: {
            page,
            limit,
        },
        // hasPhoto: true,
    }
})
const variables: ComputedRef<GemsFindStonesQueryVariables> = computed(
    (): GemsFindStonesQueryVariables => ({
        filters: {
            ...filtersVariable.value,
            hasPhoto: true,
        },
        categories: [StoneCategoryCode.Gemstone],
        notColour: [],
        notClarity: [],
        notSymmetry: [],
        getBound: true,
        getStones: page < 99,
        locale: locale.value,
        currency: currency.value,
        country: countryCode.value!,
        isConsultant: isAdmin.value,
        contentPath: '/sku-gemstones',
        facetedNav: {
            shape: filtersVariable.value.shape[0],
        },
    })
)

const response = useGemsFindStonesQuery({ variables })

const errors = useResponseError(response)

const { data, fetching, stale } = response

const prefetchPromise = response.then((res) => {
    boundsInternal.value = res.data.value?.GemsApi_getBounds || null

    return res.data
})

const findStones = toRef(() => data.value?.GemsApi_findStones)

// We might want to limit some properties on the FO for non-consultants, don't change this
const bounds = toRef(() => data.value?.GemsApi_getBounds)

const { shapes, ringShape } = useRingShapeConstraintForGemsAPI(bounds)

const priceBounds = usePriceBounds(
    bounds,
    shape,
    {
        min: 0,
        max: 5000,
    },
    defaultCurrency
)

const hasBadFacets = computed((): boolean => {
    const b = data.value?.GemsApi_getBounds

    return !!b && !!shape.value && !b.shape.includes(shape.value)
})

const { isActivated } = useActivated()

const closeStoneModal = () => {
    if (!isActivated.value) {
        return
    }

    router.push(routes.gemstoneSearch(filters.value, page))
}

const getStoneRoute = (identifier: string) =>
    routes.gemstoneSearch(filters.value, page, identifier)

const paginationTo = (p: number) => routes.gemstoneSearch(filters.value, p)

const pageWatcher = usePageWatcher(
    () => page,
    () => findStones.value?.pagination.pages ?? 0,
    () => {
        return router.resolve(paginationTo(1)).fullPath
    },
    302
)

await pathWatcher()

await prefetchPromise

if (!import.meta.env.SSR) {
    useWatchRedirectImmediate(
        () => {
            return {
                ringShape: ringShape.value,
                propsShape: shape.value,
            }
        },
        ({ ringShape, propsShape }): void => {
            if (ringShape && ringShape.systemCode !== propsShape) {
                const ringShapeRoute = getRouteShape(ringShape.systemCode)

                if (ringShapeRoute.name !== router.currentRoute.value.name) {
                    // Suspense causes you to remain on the old page so its watchers are still active
                    // But the URL/current route have moved to the next route
                    // The reason we end up here in the first place is hashParams also become empty
                    return
                }

                const route = router.resolve(ringShapeRoute)
                redirect(route.fullPath, 302)
            }
        }
    )
}

await pageWatcher()
</script>

<style lang="scss" scoped>
.checkboxes {
    margin-top: 20px;
}
</style>
